import { useState, useEffect } from 'react';
import { useNotification } from 'context/notification';
import ConfirmAction from 'components/popups/ConfirmAction';
import { LoadingState, ErrorState } from 'components/ui/states';
import HostPricingForm from 'pages/hostPricing/HostPricingForm';
import {
    deleteHostPricing,
    editHostPricing,
    editHostPricingName,
    getHostPricing,
} from './HostPricingAPI';

export default function HostPricingEditor(props) {
    const { hostId, hostName, pricingId, onUpdate, onCancel } = props;

    const [loading, setLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const [init, setInit] = useState({
        status: 'loading', // loading, error, success
        defaultValues: null,
    });

    const { notify } = useNotification();

    useEffect(() => {
        getHostPricing(hostId, pricingId)
            .then((hostPricing) => {
                const enableFeeCaps = Boolean(
                    hostPricing.b4t_fee_min_amt || hostPricing.b4t_fee_max_amt,
                );
                const enableScheduledPayouts = Boolean(hostPricing.scheduled_payout_flag);

                const allowEdit = hostPricing.sale_count === 0;
                const allowDelete =
                    hostPricing.status === 0 &&
                    hostPricing.sale_count === 0 &&
                    hostPricing.event_count === 0;

                setInit({
                    status: 'success',
                    allowEdit: allowEdit,
                    allowDelete: allowDelete,
                    defaultValues: {
                        name: hostPricing.name,
                        b4tPercent: Number((hostPricing.b4t_fee_order_pct * 100).toPrecision(6)),
                        b4tFlat: hostPricing.b4t_fee_line_item_amt,
                        feeMin: enableFeeCaps ? hostPricing.b4t_fee_min_amt : undefined,
                        feeMax: enableFeeCaps ? hostPricing.b4t_fee_max_amt : undefined,
                        ccPercent: Number((hostPricing.cc_fee_order_pct * 100).toPrecision(6)),
                        ccFlatFee: hostPricing.cc_fee_order_amt,
                        posPercent: Number(
                            (hostPricing.pos_b4t_fee_order_pct * 100).toPrecision(6),
                        ),
                        reservePercent: enableScheduledPayouts
                            ? Number((hostPricing.payout_reserve_percent * 100).toFixed(4))
                            : undefined,
                        absorbsB4tFees: hostPricing.host_absorbs_b4t_fees ? 'host' : 'customer',
                        enableFeeCaps: enableFeeCaps,
                        absorbsCcFees: hostPricing.host_absorbs_cc_fees ? 'host' : 'customer',
                        absorbsPosFees: hostPricing.pos_host_absorbs_b4t_fees ? 'host' : 'customer',
                        enableScheduledPayouts: enableScheduledPayouts,
                    },
                });
            })
            .catch((error) => {
                notify.error(String(error));
                setInit({
                    status: 'error',
                    defaultValues: null,
                });
            });
        // eslint-disable-next-line
    }, [hostId, pricingId]);

    const handleEdit = (formData) => {
        if (loading) return;
        setLoading(true);

        editHostPricing(hostId, pricingId, formData)
            .then(() => {
                notify.success('Host Pricing Updated');
                onUpdate();
            })
            .catch((error) => {
                notify.error(String(error));
                setLoading(false);
            });
    };

    const handleNameUpdate = (formData) => {
        if (loading) return;
        setLoading(true);

        editHostPricingName(hostId, pricingId, formData.name)
            .then(() => {
                notify.success('Host Pricing Updated');
                onUpdate();
            })
            .catch((error) => {
                notify.error(String(error));
                setLoading(false);
            });
    };

    const handleDelete = () => {
        setConfirmDelete(false);
        setLoading(true);

        deleteHostPricing(hostId, pricingId)
            .then(() => {
                notify.success('Deleted host pricing');
                onUpdate();
            })
            .catch((error) => {
                notify.error(String(error));
                setLoading(false);
            });
    };

    // Waiting for default values to load
    if (init.status === 'loading') {
        return <LoadingState />;
    }

    // Error loading data for product form
    if (init.status === 'error') {
        return <ErrorState message="There was a problem loading your host pricing record" />;
    }

    return (
        <>
            <HostPricingForm
                allowEdit={init.allowEdit}
                allowDelete={init.allowDelete}
                defaultValues={init.defaultValues}
                onSubmit={init.allowEdit ? handleEdit : handleNameUpdate}
                onDelete={() => setConfirmDelete(true)}
                onCancel={onCancel}
                hostName={hostName}
                loading={loading}
            />

            <ConfirmAction
                open={confirmDelete}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete(false)}
                destructive
                title="Are you sure you want to delete this pricing record?"
                description="You cannot recover this record after deleting it."
                confirmText="Delete"
            />
        </>
    );
}
